import { motion } from "framer-motion";
import { ArrowUpRight, Clock, FileText, Download, HelpCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BLOG_POSTS, FAQS, img } from "@/config";

const DOWNLOADS = [
  { title: "Company Brochure 2026", desc: "Overview of services, projects & capabilities.", size: "PDF" },
  { title: "CCTV Solutions Catalog", desc: "Camera types, specs & deployment options.", size: "PDF" },
  { title: "Security Best Practices Whitepaper", desc: "A guide to layered physical & digital security.", size: "PDF" },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Knowledge Center"
        titleLines={["Insights, guides", "& resources."]}
        subtitle="Security trends, technology deep-dives and practical guides from our engineering team — plus brochures and whitepapers to download."
        imageKey="tech_support"
      />

      {/* Blog */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20" data-testid="blog-section">
        <Reveal><h2 className="font-display text-3xl font-extrabold tracking-tight text-white lg:text-4xl">Latest articles</h2></Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {BLOG_POSTS.map((post, i) => (
            <Reveal key={post.title} delay={(i % 4) * 0.06}>
              <motion.div whileHover={{ y: -6 }}>
                <a href="#" onClick={(e) => e.preventDefault()} data-testid={`blog-card-${i}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-800 bg-[#111c33]/40">
                  <div className="aspect-video overflow-hidden">
                    <img src={img(post.image)} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#60A5FA]">
                      <span>{post.category}</span><span className="text-slate-600">•</span>
                      <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> {post.read}</span>
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold leading-snug tracking-tight text-white">{post.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{post.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white">Read more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                  </div>
                </a>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Downloads + FAQ */}
      <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-10 lg:pb-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Downloads */}
          <div data-testid="downloads-section">
            <Reveal><h2 className="flex items-center gap-2.5 font-display text-2xl font-extrabold tracking-tight text-white lg:text-3xl"><FileText className="h-6 w-6 text-[#2563EB]" /> Downloads</h2></Reveal>
            <div className="mt-8 space-y-4">
              {DOWNLOADS.map((d, i) => (
                <Reveal key={d.title} delay={i * 0.08}>
                  <a href="/brochure.pdf" download data-testid={`download-${i}`} className="group flex items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#111c33]/40 p-5 transition-colors hover:border-[#2563EB]">
                    <div>
                      <p className="font-display font-bold text-white">{d.title}</p>
                      <p className="mt-1 text-sm text-slate-400">{d.desc}</p>
                    </div>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#60A5FA] transition-colors group-hover:bg-[#2563EB] group-hover:text-white"><Download className="h-5 w-5" /></span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div data-testid="faq-section">
            <Reveal><h2 className="flex items-center gap-2.5 font-display text-2xl font-extrabold tracking-tight text-white lg:text-3xl"><HelpCircle className="h-6 w-6 text-[#2563EB]" /> Frequently asked questions</h2></Reveal>
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="rounded-2xl border border-slate-800 bg-[#111c33]/40 px-5">
                    <AccordionTrigger className="text-left font-display text-base font-semibold text-white hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
